/** @jsxImportSource @emotion/react */
import React from 'react';
import { css, jsx } from '@emotion/react';
import Table, {
  Column,
  AvatarCell,
  VisibleOnRowHover,
} from 'components/table';
import {
  FormattedMessage,
  FormattedNumber,
  FormattedDate,
  useIntl,
} from 'components/i18n';
import { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { Link } from 'react-router-dom';
import Tooltip from '@atlaskit/tooltip';
import Spinner from '@atlaskit/spinner';
import { gridSize } from '@atlaskit/theme/constants';
import Tag from '@atlaskit/tag';
import { colors } from '@atlaskit/theme';
import Button from '@atlaskit/button';
import { useAuth } from '../../auth';
import { BankAccount } from '../types';
import { MoreDropdownMenu } from '../../../components/dropdown-menu';
import { bankAccountTypes, klarnaConnectionStatus } from '../messages';
import Avatar, { CompanyAvatar } from '../../../components/avatar';

interface Props {
  bankAccounts: BankAccount[];
  isLoading: boolean;
  onEdit: (bankAccount: BankAccount) => void;
  onRemove: (bankAccount: BankAccount) => void;
  onReconnect: (bankAccount: BankAccount) => void;
  onRefreshBankData: (bankAccount: BankAccount) => void;
}

const subtleTextCss = css`
font-size: 0.85em;
color: ${colors.subtleText()};
`;

const MoreDropdownCell: React.FC<any> = React.memo(
  ({ onEdit, onRemove, onReconnect, bankAccount, onRefreshBankData }) => {
    const { isBookkeeper } = useAuth();
    const intl = useIntl();
    return (
    // <VisibleOnRowHover>
      <MoreDropdownMenu>
        {(bankAccount.klarnaConnection || bankAccount.goCardlessConnection) && (
        <DropdownItemGroup
          title={intl.formatMessage({
            id: 'bank.BankAccountsTable.options.bankConnection',
            defaultMessage: 'Bank connection',
          })}
        >
          <>
            <DropdownItem onClick={() => onRefreshBankData(bankAccount)}>
              <FormattedMessage
                id="bank.BankAccountsTable.options.refreshTransactions"
                defaultMessage="Refresh transactions"
              />
            </DropdownItem>
            <DropdownItem onClick={() => onReconnect(bankAccount)}>
              <FormattedMessage
                id="bank.BankAccountsTable.options.reconnect"
                defaultMessage="Reconnect bank account"
              />
            </DropdownItem>
          </>
        </DropdownItemGroup>
        )}
        <DropdownItemGroup
          title={intl.formatMessage({
            id: 'bank.BankAccountsTable.options.bankAccount',
            defaultMessage: 'Bank account',
          })}
        >
          <DropdownItem onClick={() => onEdit(bankAccount)}>
            <FormattedMessage
              id="bank.BankAccountsTable.options.edit"
              defaultMessage="Edit"
            />
          </DropdownItem>
          {isBookkeeper && (
          <DropdownItem onClick={() => onRemove(bankAccount)}>
            <FormattedMessage
              id="bank.BankAccountsTable.options.remove"
              defaultMessage="Remove"
            />
          </DropdownItem>
          )}
        </DropdownItemGroup>
      </MoreDropdownMenu>
    // </VisibleOnRowHover>
    );
  }
);

const BankAccountsTable = ({
  bankAccounts,
  isLoading,
  onEdit,
  onRemove,
  onReconnect,
  onRefreshBankData,
}: Props) => {
  const columns: Column<BankAccount>[] = React.useMemo(() => {
    // const EditLinkCell: React.FC<CellProps<BankAccount>> = ({row, children}) {
    //   return <Link to={'sale/'+row.id}>{children}</Link>
    // }

    return [
      {
        Header: (
          <FormattedMessage
            id="bank.BankAccountsTable.headings.account"
            defaultMessage="Account"
          />
        ),
        accessor: 'bankAccountNumber',
        minWidth: 50,
        Cell: function renderCell({
          row: {
            original: { id, bankAccountNumber, bank, bankCode, type, currency },
          },
        }) {
          return (
            <Link to={`../bank/${id}`}>
              <AvatarCell
                avatar={<div style={{color: 'initial'}}><CompanyAvatar size="medium" company={bank||{name: bankCode}} /></div>}
                secondaryText={bankAccountNumber}
                primaryText={
                  type && <FormattedMessage {...bankAccountTypes[type]} />
                }
              />
            </Link>
          );
        },
      },
      {
        Header: (
          <FormattedMessage
            id="bank.BankAccountsTable.headings.balance"
            defaultMessage="Balance"
          />
        ),
        align: 'right',
        accessor: 'bankBalance',
        width: 80,
        Cell: ({ value, row: { original: bankAccount } }) => (
          <div
            css={css`
              cursor: default;
              display: flex;
              align-items: center;
            `}
          >
            {(bankAccount?.goCardlessConnection?.isRefreshing || bankAccount?.klarnaConnection?.isRefreshing) && (
              <Tooltip content={<span>Currently refreshing bank data</span>}>
                <div
                  css={css`
                    margin-right: ${gridSize()}px;
                  `}
                >
                  <Spinner />
                </div>
              </Tooltip>
            )}
            <Tooltip
              delay={0}
              content={(
                <div>
                  Bijgewerkt tot:
                  {' '}
                  <FormattedDate value={bankAccount.bankBalanceDate} />
                </div>
              )}
            >

              <div css={css`text-align: right;`}>
                <div>
                  {' '}
                  {bankAccount.currency !== 'EUR'? `${bankAccount.currency} ` : ''}
                  <FormattedNumber value={value || bankAccount.balance} />
                </div>
                {/* <div css={subtleTextCss}><FormattedDate value={bankAccount.bankBalanceDate} /></div> */}
              </div>
            </Tooltip>
            {/*          <div css={css`font-size: 0.85em; */}
            {/* color: ${colors.subtleText()};`}> */}
            {/*            <FormattedDate value={bankAccount.updated}/></div> */}
          </div>
        ),
      },

      {
        Header: (
          <FormattedMessage
            id="bank.BankAccountsTable.headings.unprocessed"
            defaultMessage="Unprocessed"
          />
        ),
        align: 'right',
        accessor: 'unprocessedCount',
        width: 80,
        Cell: ({ value, row: { original: bankAccount } }) => (
          <Link
            to={`../bank/${bankAccount.id}`}
            css={css`
              &,
              :hover {
                color: inherit;
              }
            `}
          >
            {value}
          </Link>
        ),
      },
      {
        Header: (
          <FormattedMessage
            id="bank.BankAccountsTable.headings.connection"
            defaultMessage="Connection"
          />
        ),
        accessor: 'klarnaConnection',
        align: 'right',
        width: 80,
        Cell: ({ value: klarnaConnection, row: { original: bankAccount } }) => {
          const intl = useIntl();
          const connection = bankAccount.goCardlessConnection || klarnaConnection;
          if (!connection) {
            if (bankAccount.bank?.psd2 && bankAccount.type === 'checking_account') {
              return (
                <Button
                  spacing="compact"
                  onClick={() => onReconnect(bankAccount)}
                >
                  <FormattedMessage
                    id="bank.BankAccountsTable.bankConnection.connect"
                    defaultMessage="Connect"
                  />
                </Button>
              );
            }
            return null;
          }
          return (
            <div>
              <div
                css={css`
                  text-align: right;
                  > div > span {
                    margin: 0px;
                  }
                `}
              >
                <Tag
                  isRemovable={false}
                  text={intl.formatMessage(
                    klarnaConnectionStatus[connection.status!]
                  )}
                  color={connection.status === 'ACTIVE' ? 'standard' : 'redLight'}
                />
                <div
                  css={css`
                    font-size: 0.85em;
                    color: ${colors.subtleText()};
                  `}
                >
                  {connection.status === 'ACTIVE' ? (
                    <FormattedMessage
                      id="bank.BankAccountsTable.bankConnection.expirationDate.till"
                      defaultMessage="Till"
                    />
                  ) : (
                    <FormattedMessage
                      id="bank.BankAccountsTable.bankConnection.expirationDate.on"
                      defaultMessage="On"
                    />
                  )}
                  :
                  {' '}
                  <FormattedDate value={connection.expirationDate} />
                </div>
              </div>
            </div>
          );
        },
      },
      {
        id: 'actions',
        Header: '',
        minWidth: 56,
        maxWidth: 56,
        width: 56,
        align: 'right',
        accessor: 'id',
        noLink: true,
        Cell: ({ row: { original: bankAccount } }) => (
          <MoreDropdownCell
            onEdit={onEdit}
            onRemove={onRemove}
            onReconnect={onReconnect}
            onRefreshBankData={onRefreshBankData}
            bankAccount={bankAccount}
          />
        ),
      },
    ];
  }, []);

  return (
    <div css={css`width: 100%; table {background:white;}`}>
      <Table<BankAccount>
        name="BankAccountsTable"
        columns={columns}
        data={bankAccounts}
        isLoading={isLoading}
        onRowClick={() => alert('dd')}
        noResults={<FormattedMessage id='bank.BankAccountsTable.noResults' defaultMessage='No bank accounts found' />}
        getRowTo={(bankAccount)=> bankAccount.id}
      />
    </div>
  );
};

BankAccountsTable.defaultProps = {
  bankAccounts: [],
};

export default BankAccountsTable;
